import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, ArrowRight, ShieldCheck, AlertTriangle, Zap } from "lucide-react";
import { toast } from "sonner";
import PasswordInput from "@/components/PasswordInput";

const MIN_LEN = 8;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [reason, setReason] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setValidating(false); setValid(false); setReason("Missing reset token"); return; }
    (async () => {
      try {
        const { data } = await api.get(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`);
        setValid(!!data.valid);
        setReason(data.reason || "");
        setEmail(data.email || "");
      } catch (e) {
        setValid(false);
        setReason(formatApiError(e.response?.data?.detail) || "Could not validate");
      } finally {
        setValidating(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < MIN_LEN) { toast.error(`Password must be at least ${MIN_LEN} characters`); return; }
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password reset — please sign in");
      nav("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not reset");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white noise-bg flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Link to="/login" data-testid="reset-back-login" className="inline-flex items-center gap-2 text-xs text-[#A3A3A3] hover:text-white uppercase tracking-wider mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
        </Link>
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 bg-[#CCFF00] flex items-center justify-center"><Zap strokeWidth={2.5} className="w-5 h-5 text-black" /></div>
          <span className="h-display text-2xl tracking-tighter">RALLY LAB</span>
        </div>

        {validating && (
          <div className="label-mono text-[#A3A3A3]" data-testid="reset-validating">Validating reset link…</div>
        )}

        {!validating && !valid && (
          <div className="card-surface p-6 border-l-4 border-[#FF4500]" data-testid="reset-invalid">
            <AlertTriangle className="w-6 h-6 text-[#FF4500] mb-3" />
            <h2 className="h-display text-2xl mb-2">This link won&apos;t work.</h2>
            <p className="text-sm text-[#A3A3A3] mb-4">{reason || "The reset link is invalid or has expired."}</p>
            <Link to="/forgot-password" className="btn-volt px-5 py-3 text-xs inline-flex items-center gap-2">
              Request a new link <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}

        {!validating && valid && (
          <>
            <div className="label-mono mb-2">PASSWORD RESET</div>
            <h1 className="h-display text-4xl tracking-tighter mb-3">Choose a new password.</h1>
            <p className="text-sm text-[#A3A3A3] mb-8">For <span className="text-white">{email}</span> — minimum {MIN_LEN} characters.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <div className="label-mono mb-2">NEW PASSWORD</div>
                <PasswordInput
                  required minLength={MIN_LEN}
                  value={pw} onChange={(e) => setPw(e.target.value)}
                  data-testid="reset-pw"
                  className="w-full bg-[#141414] border border-[#262626] px-3 py-3 text-sm focus:outline-none focus:border-[#CCFF00]"
                />
              </div>
              <div>
                <div className="label-mono mb-2">CONFIRM NEW PASSWORD</div>
                <PasswordInput
                  required minLength={MIN_LEN}
                  value={pw2} onChange={(e) => setPw2(e.target.value)}
                  data-testid="reset-pw2"
                  className="w-full bg-[#141414] border border-[#262626] px-3 py-3 text-sm focus:outline-none focus:border-[#CCFF00]"
                />
              </div>
              <div className="text-xs text-[#A3A3A3] border border-[#262626] p-3 flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 text-[#CCFF00] mt-0.5 shrink-0" />
                Resetting will sign you out of any active sessions on other devices.
              </div>
              <button type="submit" disabled={saving} data-testid="reset-submit"
                className="btn-volt w-full py-3.5 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
                {saving ? "Saving…" : "Reset password"} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
